import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { mapFamilySummaries } from '@/features/families/mappers';
import { queryKeys } from '@/query/keys';
import type { FamilySummaryViewModel } from '@/models/family';

export const RECENT_FAMILIES_DEFAULT_LIMIT = 50;
export const RECENT_FAMILIES_PAGE_SIZE = 50;

export interface UseRecentFamiliesOptions {
  readonly limit?: number;
}

/**
 * Families the current user can access, mapped to the UI view-model shape.
 * Wraps `GET /families/search` with no criteria — PTV2-1185 made
 * `name` / `custom_label` optional, so the search endpoint now doubles as
 * the canonical all-families listing.
 *
 * Pagination: the live endpoint pages through `page` + `size`, but the FE
 * keeps the patient-list pattern of progressive `size` growth so prior rows
 * stay visible while the bigger fetch is in flight.
 */
export function useRecentFamilies(
  options: UseRecentFamiliesOptions = {},
): UseQueryResult<readonly FamilySummaryViewModel[]> {
  const limit = options.limit ?? RECENT_FAMILIES_DEFAULT_LIMIT;
  return useQuery<readonly FamilySummaryViewModel[]>({
    queryKey: queryKeys.recentFamilies(limit),
    queryFn: async ({ signal }) => {
      const rows = await apiClient.searchFamilies({ size: limit }, { signal });
      return mapFamilySummaries(rows);
    },
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}
